import React, { useEffect, useState } from "react";
import { Presentation, Youtube, Link as LinkIcon, ExternalLink, Download, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * All resources teachers have attached to a single chapter, grouped into
 * Presentations / Videos / Resources. Visible to every approved student;
 * read-only for them.
 */
export default function ChapterResources({ chapterId }) {
  const { isRTL } = useLanguage();
  const [data, setData] = useState({ presentations: [], videos: [], links: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/me/chapters/${chapterId}/resources`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* student sees empty state if endpoint fails */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId]);

  const total = data.presentations.length + data.videos.length + data.links.length;

  if (loading) return null;

  if (total === 0) {
    return (
      <section className="bg-white border border-dashed border-border rounded-2xl p-8 text-center" data-testid="chapter-resources-empty">
        <div className="mx-auto w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center mb-3">
          <Presentation className="w-6 h-6" strokeWidth={1.7} />
        </div>
        <div className="font-display text-lg font-semibold mb-1">
          {isRTL ? "لم يُضِف المعلّمون موارد لهذا الفصل بعد" : "No teacher resources for this chapter yet"}
        </div>
        <div className="text-sm text-muted-foreground max-w-md mx-auto">
          {isRTL
            ? "بمجرد أن يضيف معلّموك عروضاً تقديمية أو فيديوهات أو روابط مرجعية لهذا الفصل، ستظهر هنا تلقائياً."
            : "When your teachers attach presentations, videos, or reference links to this chapter, they'll show up here automatically."}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6" data-testid="chapter-resources">
      <ResourceSection
        icon={Presentation}
        iconClass="text-terracotta"
        bgClass="bg-terracotta/10"
        title={isRTL ? "العروض التقديمية والملفات" : "Presentations & files"}
        items={data.presentations}
        renderItem={(r) => <PresentationCard key={r.resource_id} resource={r} />}
        emptyLabel={isRTL ? "لا توجد عروض" : "No presentations yet"}
        testid="chapter-resources-presentations"
      />
      <ResourceSection
        icon={Youtube}
        iconClass="text-[#d8382b]"
        bgClass="bg-[#d8382b]/10"
        title={isRTL ? "فيديوهات" : "Videos"}
        items={data.videos}
        renderItem={(r) => <VideoCard key={r.resource_id} resource={r} />}
        emptyLabel={isRTL ? "لا توجد فيديوهات" : "No videos yet"}
        testid="chapter-resources-videos"
      />
      <ResourceSection
        icon={LinkIcon}
        iconClass="text-sage"
        bgClass="bg-sage/10"
        title={isRTL ? "روابط ومراجع" : "Reference links"}
        items={data.links}
        renderItem={(r) => <LinkCard key={r.resource_id} resource={r} />}
        emptyLabel={isRTL ? "لا توجد روابط" : "No links yet"}
        testid="chapter-resources-links"
      />
    </section>
  );
}

function ResourceSection({ icon: Icon, iconClass, bgClass, title, items, renderItem, emptyLabel, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg ${bgClass} ${iconClass} flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground ms-1">· {items.length}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{items.map(renderItem)}</div>
      <span className="sr-only">{emptyLabel}</span>
    </div>
  );
}

function MetaRow({ resource: r }) {
  return (
    <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
      <span>{r.teacher_email}</span>
      <span className="opacity-50">·</span>
      <span className="inline-flex items-center gap-1">
        <Calendar className="w-3 h-3" />
        {new Date(r.created_at).toLocaleDateString()}
      </span>
    </div>
  );
}

function PresentationCard({ resource: r }) {
  const fileUrl = r.file_id ? `${BACKEND}/api/files/${r.file_id}` : null;
  return (
    <div className="bg-white border border-border rounded-2xl p-4 flex flex-col gap-2.5" data-testid={`chapter-pres-${r.resource_id}`}>
      <div className="font-medium leading-tight line-clamp-2">{r.title}</div>
      {r.description && <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>}
      <MetaRow resource={r} />
      {fileUrl && (
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-terracotta/10 hover:bg-terracotta/20 text-terracotta text-xs font-medium"
          data-testid={`chapter-pres-open-${r.resource_id}`}
        >
          <Download className="w-3.5 h-3.5" /> Open
        </a>
      )}
    </div>
  );
}

function VideoCard({ resource: r }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 flex flex-col gap-2.5" data-testid={`chapter-vid-${r.resource_id}`}>
      {r.youtube_id && (
        <div className="aspect-video rounded-lg overflow-hidden bg-bone border border-border">
          <iframe
            src={`https://www.youtube.com/embed/${r.youtube_id}`}
            title={r.title}
            className="w-full h-full"
            allowFullScreen
          />
        </div>
      )}
      <div className="font-medium leading-tight line-clamp-2">{r.title}</div>
      {r.description && <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>}
      <MetaRow resource={r} />
    </div>
  );
}

function LinkCard({ resource: r }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 flex flex-col gap-2.5" data-testid={`chapter-link-${r.resource_id}`}>
      <div className="font-medium leading-tight line-clamp-2">{r.title}</div>
      {r.description && <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>}
      <MetaRow resource={r} />
      {r.external_url && (
        <a
          href={r.external_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium"
          data-testid={`chapter-link-open-${r.resource_id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" /> Visit link
        </a>
      )}
    </div>
  );
}
